import React, { useState } from 'react';
import PropTypes from 'prop-types';
import styles from './CardDmc.module.scss';
import AnimateHeight from 'react-animate-height';
import classNames from 'classnames';
import { useTranslation } from 'next-i18next';

import Wysiwyg from '../Wysiwyg';
import BackgroundImage from '../BackgroundImage';

import { serializeImage } from '../../utils/SerializeImage';

const CardDmc = ({
    id,
    image,
    title,
    website,
    richText,
    contact,
    moreLink,
}) => {
    const { t } = useTranslation();

    const [isOpen, setIsOpen] = useState(false);
    const [isContactOpen, setIsContactOpen] = useState(false);
    const handleExpand = () => {
        setIsOpen(!isOpen);
    };
    const handleContactExapand = () => {
        setIsContactOpen(!isContactOpen);
    };

    const serializedImage = serializeImage(image, true);
    const imageSizes = '(min-width: 768px) 190px, 165px';

    const classes = classNames(styles['CardDmc'], {
        [styles['CardDmc--Expanded']]: isOpen,
    });
    const contactButton = classNames(styles['CardDmc__ContactButton'], {
        [styles['CardDmc__ContactButton--ContactExpanded']]: isContactOpen,
    });

    const buttonTitle = !isOpen ? t('cardDmc.expand') : t('cardDmc.shrink');

    return (
        <div className={classes}>
            {!!image && (
                <div className={styles['CardDmc__Image']}>
                    <BackgroundImage
                        {...serializedImage}
                        fullSize={true}
                        focal={{ x: '50%', y: '0%' }}
                        sizes={imageSizes}
                        quality={50}
                        modifiers={['CardDmc']}
                    />
                </div>
            )}
            <div className={styles['CardDmc__Container']}>
                <div className={styles['CardDmc__Content']}>
                    <div className={styles['CardDmc__Header']}>
                        <h3 className={styles['CardDmc__Title']}>{title}</h3>
                        {!!website && website.href && (
                            <a
                                href={website.href}
                                className={styles['CardDmc__ExternalLink']}
                                target="_blank"
                                rel="noopener"
                            >
                                {website.title || website.href}
                            </a>
                        )}
                    </div>
                    <button
                        aria-controls={`card-dmc-${id}`}
                        type="button"
                        onClick={handleExpand}
                        className={styles['CardDmc__Expand']}
                    >
                        {buttonTitle}
                    </button>
                    <AnimateHeight
                        id={`card-dmc-${id}`}
                        height={isOpen ? 'auto' : 30}
                        duration={450}
                    >
                        {richText && (
                            <Wysiwyg
                                html={richText}
                                isExpandable={false}
                                modifiers={[
                                    'ExpandableSmall',
                                    'NoMarginBottom',
                                    'Dmc',
                                    isOpen ? 'DmcExpanded' : '',
                                ]}
                            />
                        )}
                    </AnimateHeight>
                </div>
                {!!contact && (
                    <div className={styles['CardDmc__ContactContainer']}>
                        <button
                            aria-controls={`card-dmc-${id}-contact`}
                            type="button"
                            onClick={handleContactExapand}
                            className={contactButton}
                        >
                            {t('cardDmc.contact')}
                        </button>
                        <div className={styles['CardDmc__ContactWrapper']}>
                            <AnimateHeight
                                id={`card-dmc-${id}-contact`}
                                className={styles['CardDmc__ContactContent']}
                                height={isContactOpen ? 'auto' : 0}
                                duration={450}
                            >
                                {contact.name && (
                                    <div className={styles['CardDmc__Contact']}>
                                        <span>{t('cardDmc.name')}</span>
                                        {contact.name}
                                    </div>
                                )}
                                {contact.email && (
                                    <div className={styles['CardDmc__Contact']}>
                                        <span>{t('cardDmc.email')}</span>
                                        <a
                                            href={`mailto:${contact.email}`}
                                            className={styles['CardDmc__Link']}
                                        >
                                            {contact.email}
                                        </a>
                                    </div>
                                )}
                                {contact.phone && (
                                    <div className={styles['CardDmc__Contact']}>
                                        <span>{t('cardDmc.phone')}</span>
                                        <a
                                            href={`tel:${contact.phone}`}
                                            className={styles['CardDmc__Link']}
                                        >
                                            {contact.phone}
                                        </a>
                                    </div>
                                )}
                                {contact.adress && (
                                    <div className={styles['CardDmc__Contact']}>
                                        <span>{t('cardDmc.adress')}</span>
                                        {contact.adress}
                                    </div>
                                )}
                            </AnimateHeight>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

CardDmc.propTypes = {
    image: PropTypes.object,
    title: PropTypes.string,
    website: PropTypes.object,
    richText: PropTypes.string,
    contact: PropTypes.object,
    moreLink: PropTypes.object,
};

CardDmc.defaultProps = {
    image: {},
    title: '',
    website: {},
    richText: '',
    contact: {},
    moreLink: {},
};

export default CardDmc;
